import { AppActions, Area, Rack, Shelf, Table, CheckoutZoneData, ZoneSummary } from '../app/appContext';
import { getInstantFrame, getLatestFrame } from '../api/frames';
import { listAreasByCamera, listAreasByZone } from '../api/areas';
import { listRacksByAreas } from '../api/racks';
import { listShelvesByAreas } from '../api/shelves';
import { listTablesByAreas } from '../api/tables';
import {
  listCheckoutZones,
  listCheckoutQueues,
  listStaffZones,
  listPolygonGroups,
} from '../api/checkoutControl';
import { toAbsoluteUrl } from '../api/url';
import { API_HOST } from '../api/base';

// ── Mappers ──────────────────────────────────────────────────────────────────

function mapArea(a: any): Area {
  return {
    id: a.id,
    points: a.direction_vectors || [],
    type: a.type,
    camera: a.camera,
    name: a.name,
  };
}

function mapRack(r: any): Rack {
  return {
    id: r.id,
    area: r.area,
    shelves: r.shelves,
    name: r.name,
    rack_type: r.rack_type,
    perspective_width: r.perspective_width,
    perspective_height: r.perspective_height,
    initial_width: r.initial_width,
    initial_height: r.initial_height,
    is_composite: r.is_composite,
    predict_percentage: r.predict_percentage,
    single_shelf: Boolean(r.single_shelf),
    merchandising_dashboard_id: r.merchandising_dashboard_id,
    is_archived: r.is_archived,
  };
}

function mapShelf(s: any): Shelf {
  return {
    id: s.id,
    area: s.area,
    rack: s.rack,
    name: s.name,
    bottom_points_indices: s.bottom_points_indices,
    top_points_indices: s.top_points_indices,
    perspective_width: s.perspective_width,
    perspective_height: s.perspective_height,
    empty_shelf_score: s.empty_shelf_score,
    shelf_num_on_rack: s.shelf_num_on_rack,
    max_effective_percentage: s.max_effective_percentage,
  };
}

function mapTable(t: any): Table {
  return {
    id: t.id,
    area: t.area,
    name: t.name,
    perspective_width: t.perspective_width,
    perspective_height: t.perspective_height,
    // Флаги формы стола. Без них форма показывала дефолты и при любом сохранении
    // возвращала «можно сдвинуть» = true даже у барной стойки, где галочку сняли.
    can_be_physically_moved: t.can_be_physically_moved,
    spoiled_glare_only: t.spoiled_glare_only,
    crop_brighten: t.crop_brighten,
    is_archived: t.is_archived,
  };
}

// ── Shared entity loader ─────────────────────────────────────────────────────

async function loadEntitiesForAreas(
  areaIds: string[],
  actions: AppActions,
): Promise<void> {
  if (areaIds.length === 0) {
    actions.setRacks([]);
    actions.setShelves([]);
    actions.setTables([]);
    actions.setCheckoutZones([]);
    return;
  }

  // Racks and shelves (required)
  const [racksRes, shelvesRes] = await Promise.all([
    listRacksByAreas(areaIds),
    listShelvesByAreas(areaIds),
  ]);
  const racks = racksRes.data.results.map(mapRack);
  const shelves = shelvesRes.data.results.map(mapShelf);

  // Tables (optional — table_control may not be deployed)
  let tables: Table[] = [];
  try {
    const tablesRes = await listTablesByAreas(areaIds);
    tables = tablesRes.data.results.map(mapTable);
  } catch {
    console.warn('table_control not available, skipping tables');
  }

  // Checkout zones (optional — checkout_control may not be deployed)
  const checkoutZones: CheckoutZoneData[] = [];
  try {
    const [czRes, cqRes, szRes, pgRes] = await Promise.all([
      listCheckoutZones(),
      listCheckoutQueues(),
      listStaffZones(),
      listPolygonGroups(),
    ]);

    const areaIdSet = new Set(areaIds.map(String));

    const groupMap = new Map<string, string[]>();
    for (const pg of pgRes.data.results) {
      groupMap.set(String(pg.id), (pg.areas || []).map(String));
    }

    const queueMap = new Map<string, { id: string; max_allowed_size: number }>();
    for (const cq of cqRes.data.results) {
      queueMap.set(String(cq.checkout_zone), { id: String(cq.id), max_allowed_size: cq.max_allowed_size });
    }

    const staffMap = new Map<string, { id: string; max_workers: number }>();
    for (const sz of szRes.data.results) {
      staffMap.set(String(sz.checkout_zone), { id: String(sz.id), max_workers: sz.max_workers });
    }

    for (const cz of czRes.data.results) {
      const groupAreas = groupMap.get(String(cz.group)) || [];
      const hasAreaOnCamera = groupAreas.some(aid => areaIdSet.has(aid));
      if (!hasAreaOnCamera) continue;

      checkoutZones.push({
        id: String(cz.id),
        group: String(cz.group),
        name: cz.name,
        type: cz.type,
        operations_dashboard_id: cz.operations_dashboard_id,
        areas: groupAreas,
        queue: queueMap.get(String(cz.id)) || null,
        staffZone: staffMap.get(String(cz.id)) || null,
      });
    }
  } catch {
    console.warn('checkout_control not available, skipping checkout zones');
  }

  actions.setRacks(racks);
  actions.setShelves(shelves);
  actions.setTables(tables);
  actions.setCheckoutZones(checkoutZones);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Reload polygon data only (areas + entities) for a given camera.
 * Used after save/delete operations to refresh the canvas.
 */
export async function loadPolygonData(cameraId: string, actions: AppActions) {
  try {
    const areasRes = await listAreasByCamera(cameraId);
    const areas = areasRes.data.results.map(mapArea);
    actions.setAreas(areas);

    const areaIds = areas.map(a => String(a.id));
    await loadEntitiesForAreas(areaIds, actions);
  } catch (error) {
    console.error('Failed to load polygon data:', error);
    actions.setAreas([]);
    actions.setRacks([]);
    actions.setShelves([]);
    actions.setTables([]);
    actions.setCheckoutZones([]);
    throw error;
  }
}

/**
 * Full camera load: frame + areas + entities.
 * Used on initial camera selection.
 */
export async function loadCameraData(
  cameraId: string,
  actions: AppActions,
  options?: { useInstant?: boolean }
) {
  try {
    // Fetch frame (may be unavailable)
    let frameData: any = null;
    try {
      const frameRes = options?.useInstant ? getInstantFrame(cameraId) : getLatestFrame(cameraId);
      frameData = (await frameRes).data;
    } catch {
      console.warn('Failed to load frame, continuing without image');
    }

    if (frameData?.image) {
      const imageUrl = toAbsoluteUrl(frameData.image, API_HOST || undefined);
      actions.setFrame({ url: imageUrl, width: 1280, height: 720, id: frameData.id });
    }
    // If frame fetch failed, keep existing frame (don't clear)

    // Fetch areas + entities (shared logic)
    const areasRes = await listAreasByCamera(cameraId);
    const areas = areasRes.data.results.map(mapArea);
    actions.setAreas(areas);

    const areaIds = areas.map(a => String(a.id));
    await loadEntitiesForAreas(areaIds, actions);
  } catch (error) {
    console.error('Failed to load camera data:', error);
    // Don't clear frame — keep old/cached frame visible
    actions.setAreas([]);
    actions.setRacks([]);
    actions.setShelves([]);
    actions.setTables([]);
    actions.setCheckoutZones([]);
    throw error;
  }
}

/**
 * Load a summary of ALL polygons across all cameras in a branch/zone.
 * Used by the sidebar and form dropdowns to show branch-scoped data.
 */
export async function loadZoneSummary(zoneId: string, actions: AppActions): Promise<void> {
  actions.setZoneSummaryLoading(true);
  try {
    // One call to get all areas for this zone
    const areasRes = await listAreasByZone(zoneId);
    const allAreas = areasRes.data.results.map(mapArea);
    const areaCameraMap: Record<string, string> = {};
    for (const a of allAreas) areaCameraMap[String(a.id)] = String(a.camera);

    const allAreaIds = allAreas.map(a => String(a.id));
    if (allAreaIds.length === 0) {
      actions.setZoneSummary({ areas: [], racks: [], shelves: [], tables: [], checkoutZones: [], areaCameraMap: {} });
      return;
    }

    // All entity calls in parallel
    const [racksRes, shelvesRes, tablesRes, czRes, cqRes, szRes, pgRes] = await Promise.all([
      listRacksByAreas(allAreaIds),
      listShelvesByAreas(allAreaIds),
      listTablesByAreas(allAreaIds).catch(() => ({ data: { results: [] } })),
      listCheckoutZones().catch(() => ({ data: { results: [] } })),
      listCheckoutQueues().catch(() => ({ data: { results: [] } })),
      listStaffZones().catch(() => ({ data: { results: [] } })),
      listPolygonGroups().catch(() => ({ data: { results: [] } })),
    ]);

    const racks = racksRes.data.results.map(mapRack);
    const shelves = shelvesRes.data.results.map(mapShelf);
    const tables = ((tablesRes as any).data.results || []).map(mapTable);

    const areaIdSet = new Set(allAreaIds);
    const groupMap = new Map<string, string[]>();
    for (const pg of ((pgRes as any).data.results || [])) groupMap.set(String(pg.id), (pg.areas || []).map(String));
    const queueMap = new Map<string, { id: string; max_allowed_size: number }>();
    for (const cq of ((cqRes as any).data.results || [])) queueMap.set(String(cq.checkout_zone), { id: String(cq.id), max_allowed_size: cq.max_allowed_size });
    const staffMap = new Map<string, { id: string; max_workers: number }>();
    for (const sz of ((szRes as any).data.results || [])) staffMap.set(String(sz.checkout_zone), { id: String(sz.id), max_workers: sz.max_workers });

    const checkoutZones: CheckoutZoneData[] = [];
    for (const cz of ((czRes as any).data.results || [])) {
      const groupAreas = groupMap.get(String(cz.group)) || [];
      if (!groupAreas.some((aid: string) => areaIdSet.has(aid))) continue;
      checkoutZones.push({
        id: String(cz.id), group: String(cz.group), name: cz.name, type: cz.type,
        operations_dashboard_id: cz.operations_dashboard_id, areas: groupAreas,
        queue: queueMap.get(String(cz.id)) || null, staffZone: staffMap.get(String(cz.id)) || null,
      });
    }

    actions.setZoneSummary({ areas: allAreas, racks, shelves, tables, checkoutZones, areaCameraMap });
  } catch (error) {
    console.error('[loadZoneSummary] FAILED:', error);
    actions.setZoneSummary({ areas: [], racks: [], shelves: [], tables: [], checkoutZones: [], areaCameraMap: {} });
  } finally {
    actions.setZoneSummaryLoading(false);
  }
}
